var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var _a;
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useState } from "react";
import { Box, Checkbox, FormControl, FormControlLabel, FormLabel, MenuItem, Radio, RadioGroup, TextField, Typography, } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { useGetList } from "react-admin";
import { Dialog } from "@sequentech/ui-essentials";
import { zoneLabel, zonedToInstant } from "@sequentech/ui-core";
import { EXPORT_ELECTION_EVENT_LOGS } from "@/queries/ExportElectionEventLogs";
import { EXPORT_TENANT_LOGS } from "@/queries/ExportTenantLogs";
import { IPermissions } from "@/types/keycloak";
import { useWidgetStore } from "@/providers/WidgetsContextProvider";
import { ETasksExecution } from "@/types/tasksExecution";
import { useAliasRenderer } from "@/hooks/useAliasRenderer";
import { formatWallTime, useTimeZoneService } from "@/components/timezones/timeZoneService";
import { TimeZonePicker } from "@/components/timezones/TimeZonePicker";
import { EExportLogsFormat, EExportLogsScope, eventExportVariables, exportLogsScopes, exportScopeReady, isAccountScope, tenantExportScope, tenantExportVariables, } from "./exportLogsRequest";
import { ELogScope } from "./logScope";
/** The zone choice that keeps each row in its own log zone (no `time_zone` sent). */
var ROW_ZONES = "row-zones";
var PICKER_PAGE = { page: 1, perPage: 1000 };
var FORMATS = [EExportLogsFormat.CSV, EExportLogsFormat.SQL, EExportLogsFormat.PDF];
var FORMAT_LABELS = (_a = {},
    _a[EExportLogsFormat.CSV] = "logsScreen.exportdialog.csv",
    _a[EExportLogsFormat.SQL] = "logsScreen.exportdialog.sql",
    _a[EExportLogsFormat.PDF] = "logsScreen.exportdialog.pdf",
    _a);
/** Incomplete/invalid bounds must not become an omitted export range. */
var resolveBound = function (local, zone) {
    if (!local || !zone)
        return null;
    try {
        return zonedToInstant(local.slice(0, 16), zone);
    }
    catch (_a) {
        return null;
    }
};
/**
 * The Logs export: what it covers (the whole log, or one election, area or
 * account of it), a range of Created (both ends included, to the minute), the
 * zone the range and the file's times are in, and CSV, SQL or PDF.
 */
export var ExportLogsDialog = function (_a) {
    var _b, _c;
    var log = _a.log, open = _a.open, onClose = _a.onClose, zones = _a.zones, _d = _a.byElection, byElection = _d === void 0 ? false : _d;
    var _e = useTranslation(), t = _e.t, i18n = _e.i18n;
    var lang = i18n.language;
    var service = useTimeZoneService();
    var aliasRenderer = useAliasRenderer();
    var tenantLog = log.kind === ELogScope.TENANT;
    var electionEventId = log.kind === ELogScope.EVENT ? log.electionEventId : "";
    var scopes = exportLogsScopes(log.kind);
    var _f = useState(""), from = _f[0], setFrom = _f[1];
    var _g = useState(""), to = _g[0], setTo = _g[1];
    // Until someone chooses, the default follows the log's zones.
    var _h = useState(null), chosen = _h[0], setChosen = _h[1];
    var _j = useState(EExportLogsFormat.CSV), format = _j[0], setFormat = _j[1];
    var _k = useState(scopes[0]), scope = _k[0], setScope = _k[1];
    var _l = useState(""), username = _l[0], setUsername = _l[1];
    var _m = useState(false), withElectionEvents = _m[0], setWithElectionEvents = _m[1];
    var _o = useState(null), electionId = _o[0], setElectionId = _o[1];
    var _p = useState(null), areaId = _p[0], setAreaId = _p[1];
    var choice = chosen !== null && chosen !== void 0 ? chosen : (byElection ? ROW_ZONES : ((_b = zones[0]) !== null && _b !== void 0 ? _b : ""));
    // Each row in its own zone: the range is read in the primary.
    var rowZones = choice === ROW_ZONES;
    var zone = rowZones ? ((_c = zones[0]) !== null && _c !== void 0 ? _c : "") : choice;
    var exportEventLogs = useMutation(EXPORT_ELECTION_EVENT_LOGS, {
        context: { headers: { "x-hasura-role": IPermissions.LOGS_EXPORT } },
    })[0];
    var exportTenantLogs = useMutation(EXPORT_TENANT_LOGS, {
        context: { headers: { "x-hasura-role": IPermissions.TENANT_LOGS_EXPORT } },
    })[0];
    var elections = useGetList("sequent_backend_election", {
        filter: { election_event_id: electionEventId },
        pagination: PICKER_PAGE,
        sort: { field: "created_at", order: "ASC" },
    }, { enabled: open && !!electionEventId && scope === EExportLogsScope.ELECTION }).data;
    var areas = useGetList("sequent_backend_area", {
        filter: { election_event_id: electionEventId },
        pagination: PICKER_PAGE,
        sort: { field: "name", order: "ASC" },
    }, { enabled: open && !!electionEventId && scope === EExportLogsScope.AREA }).data;
    var _q = useWidgetStore(), addWidget = _q[0], setWidgetTaskId = _q[1], updateWidgetFail = _q[2];
    var start = resolveBound(from, zone);
    var end = resolveBound(to, zone);
    var invalidFrom = !!from && (!start || start.kind === "gap");
    var invalidTo = !!to && (!end || end.kind === "gap");
    var validRange = !!zone &&
        !invalidFrom &&
        !invalidTo &&
        (!start || !end || Date.parse(start.instant) <= Date.parse(end.instant));
    var request = {
        scope: tenantLog ? tenantExportScope(scope, withElectionEvents) : scope,
        username: username,
        electionId: electionId,
        areaId: areaId,
    };
    var ready = validRange && exportScopeReady(request);
    var boundNote = function (local, resolved) {
        if (!local)
            return undefined;
        if (!resolved)
            return t("lifecycle.import.error.invalidDateTime");
        if (resolved.kind === "gap")
            return t("lifecycle.import.error.dstGap", {
                dateTime: formatWallTime(local, service.text),
                city: service.zoneCity(zone, service.text),
            });
        return service.zonedTimeNote(local, zone, service.text);
    };
    var download = function () { return __awaiter(void 0, void 0, void 0, function () {
        var range, widget, _a, data_1, errors_1, _b, data, errors, _c;
        var _d, _e, _f, _g;
        return __generator(this, function (_h) {
            switch (_h.label) {
                case 0:
                    if (!ready)
                        return [2 /*return*/];
                    range = {
                        createdFrom: (_d = start === null || start === void 0 ? void 0 : start.instant) !== null && _d !== void 0 ? _d : null,
                        createdTo: (_e = end === null || end === void 0 ? void 0 : end.instant) !== null && _e !== void 0 ? _e : null,
                        timeZone: rowZones ? null : zone || null,
                    };
                    widget = addWidget(tenantLog
                        ? ETasksExecution.EXPORT_TENANT_LOGS
                        : ETasksExecution.EXPORT_ACTIVITY_LOGS_REPORT, true);
                    _h.label = 1;
                case 1:
                    _h.trys.push([1, 5, , 6]);
                    if (!tenantLog) return [3 /*break*/, 3];
                    return [4 /*yield*/, exportTenantLogs({
                            variables: tenantExportVariables(request, format, range),
                        })];
                case 2:
                    _a = _h.sent(), data_1 = _a.data, errors_1 = _a.errors;
                    if (errors_1) {
                        updateWidgetFail(widget.identifier);
                        return [2 /*return*/];
                    }
                    setWidgetTaskId(widget.identifier, (_f = data_1 === null || data_1 === void 0 ? void 0 : data_1.export_tenant_logs) === null || _f === void 0 ? void 0 : _f.task_execution.id);
                    return [2 /*return*/];
                case 3: return [4 /*yield*/, exportEventLogs({
                        variables: eventExportVariables(electionEventId, request, format, range),
                    })];
                case 4:
                    _b = _h.sent(), data = _b.data, errors = _b.errors;
                    if (errors) {
                        updateWidgetFail(widget.identifier);
                        return [2 /*return*/];
                    }
                    setWidgetTaskId(widget.identifier, (_g = data === null || data === void 0 ? void 0 : data.export_election_event_logs) === null || _g === void 0 ? void 0 : _g.task_execution.id);
                    return [3 /*break*/, 6];
                case 5:
                    _c = _h.sent();
                    updateWidgetFail(widget.identifier);
                    return [3 /*break*/, 6];
                case 6: return [2 /*return*/];
            }
        });
    }); };
    // The label at the range's start (its offset there), else now.
    var abbr = zone
        ? zoneLabel(zone, { t: t, lang: lang }, start ? new Date(start.instant) : new Date())
        : "";
    var printed = format === EExportLogsFormat.PDF;
    var note = rowZones
        ? printed
            ? "logsScreen.exportdialog.zoneNoteRowsPdf"
            : "logsScreen.exportdialog.zoneNoteRows"
        : printed
            ? "logsScreen.exportdialog.zoneNotePdf"
            : "logsScreen.exportdialog.zoneNote";
    var scopeLabel = String(t("logsScreen.exportdialog.scope.label"));
    var usernameLabel = String(t("logsScreen.exportdialog.username"));
    var electionLabel = String(t("logsScreen.exportdialog.election"));
    var areaLabel = String(t("logsScreen.exportdialog.area"));
    return (_jsx(Dialog, { variant: "info", open: open, fullWidth: true, maxWidth: "sm", ok: String(t("common.label.export")), cancel: String(t("common.label.cancel")), title: String(t("logsScreen.exportdialog.title")), okEnabled: function () { return ready; }, handleClose: function (result) {
            onClose();
            if (result)
                download();
        }, children: _jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: 2, pt: 1 }, children: [_jsx(TextField, { select: true, label: scopeLabel, value: scope, onChange: function (event) { return setScope(event.target.value); }, className: "export-logs-scope", children: scopes.map(function (option) { return (_jsx(MenuItem, { value: option, children: t("logsScreen.exportdialog.scope.".concat(log.kind, ".").concat(option)) }, option)); }) }), scope === EExportLogsScope.ELECTION ? (_jsx(TextField, { select: true, required: true, label: electionLabel, value: electionId !== null && electionId !== void 0 ? electionId : "", onChange: function (event) { return setElectionId(event.target.value || null); }, children: (elections !== null && elections !== void 0 ? elections : []).map(function (election) { return (_jsx(MenuItem, { value: election.id, children: aliasRenderer(election) }, election.id)); }) })) : null, scope === EExportLogsScope.AREA ? (_jsx(TextField, { select: true, required: true, label: areaLabel, value: areaId !== null && areaId !== void 0 ? areaId : "", onChange: function (event) { return setAreaId(event.target.value || null); }, children: (areas !== null && areas !== void 0 ? areas : []).map(function (area) { return (_jsx(MenuItem, { value: area.id, children: area.name }, area.id)); }) })) : null, isAccountScope(scope) ? (_jsx(TextField, { required: true, label: usernameLabel, value: username, onChange: function (event) { return setUsername(event.target.value); }, autoComplete: "off" })) : null, tenantLog && isAccountScope(scope) ? (_jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: withElectionEvents, onChange: function (event) { return setWithElectionEvents(event.target.checked); } }), label: t("logsScreen.exportdialog.withElectionEvents") })) : null, _jsxs(Box, { sx: { display: "flex", gap: 2, flexWrap: "wrap" }, children: [_jsx(TextField, { type: "datetime-local", label: t("logsScreen.exportdialog.from"), value: from, error: invalidFrom, helperText: boundNote(from, start), onChange: function (event) { return setFrom(event.target.value); }, InputLabelProps: { shrink: true }, inputProps: { "aria-label": String(t("logsScreen.exportdialog.from")) }, sx: { flex: 1, minWidth: 200 } }), _jsx(TextField, { type: "datetime-local", label: t("logsScreen.exportdialog.to"), value: to, error: invalidTo, helperText: boundNote(to, end), onChange: function (event) { return setTo(event.target.value); }, InputLabelProps: { shrink: true }, inputProps: { "aria-label": String(t("logsScreen.exportdialog.to")) }, sx: { flex: 1, minWidth: 200 } })] }), _jsx(TimeZonePicker, { label: t("logsScreen.exportdialog.timeZone"), value: choice, zones: zones, primary: zones[0], onChange: function (zone) {
                        if (zone)
                            setChosen(zone);
                    }, additionalOptions: byElection
                        ? [
                            {
                                zone: ROW_ZONES,
                                label: t("logsScreen.exportdialog.rowZones"),
                                detail: "",
                                offsetMinutes: 0,
                            },
                        ]
                        : [] }), _jsxs(FormControl, { children: [_jsx(FormLabel, { id: "export-logs-format", children: t("logsScreen.exportdialog.format") }), _jsx(RadioGroup, { row: true, "aria-labelledby": "export-logs-format", value: format, onChange: function (event) { return setFormat(event.target.value); }, children: FORMATS.map(function (option) { return (_jsx(FormControlLabel, { value: option, control: _jsx(Radio, {}), label: t(FORMAT_LABELS[option]) }, option)); }) })] }), abbr ? (_jsxs(Typography, { variant: "body2", className: "export-logs-note", children: [_jsx("span", { children: t(note, { abbr: abbr }) }), " ", request.scope === EExportLogsScope.ACCOUNT_WITH_EVENTS ? (_jsxs(_Fragment, { children: [_jsx("span", { children: t("logsScreen.exportdialog.rowsNameLog") }), " "] })) : null, _jsx("span", { children: t("logsScreen.exportdialog.recorded") })] })) : null] }) }));
};
